(() => {
  if (window.__FSB_BOUND__) return;
  window.__FSB_BOUND__ = true;

  // ── Constants ──────────────────────────────────────────────────────────
  const COLOR    = 'rgb(188 158 244)';
  const ANIM     = 'transform 650ms cubic-bezier(0.22, 1, 0.36, 1)';
  const SETTLE_MS = 220;

  // ── State ──────────────────────────────────────────────────────────────
  let lastProgress  = null;   // 0..100
  let lastItemCount = null;
  let lastFillEl    = null;

  let settleTimer = null;
  let inFlight    = false;
  let pending     = false;

  // ── DOM helpers ────────────────────────────────────────────────────────
  function getWrap() {
    return document.querySelector('cart-drawer .gfb-wrap[data-gfb-large]');
  }

  function getEls() {
    const wrap = getWrap();
    if (!wrap) return null;
    return {
      wrap,
      large : Number(wrap.dataset.gfbLarge || 0),
      small : Number(wrap.dataset.gfbSmall  || 0),
      fill  : wrap.querySelector('.gfb-fill'),
      text  : wrap.querySelector('.gfb-text'),
    };
  }

  // ── Progress math ──────────────────────────────────────────────────────
  function computeProgress(totalCents, largeCents) {
    if (!largeCents) return 0;
    let p = (totalCents / largeCents) * 100;
    p = Math.min(Math.max(p, 0), 100);
    p = Math.round(p * 10) / 10;
    if (p > 0 && p < 1) p = 1;
    return p;
  }

  // ── Money formatter ────────────────────────────────────────────────────
  function formatMoney(cents) {
    // Ensuring exactly 2 decimal digits as requested
    const amount = (cents / 100).toFixed(2);
    // Shekel symbol on the right without space
    return amount + '₪';
  }

  // ── Text message ───────────────────────────────────────────────────────
  function buildText(wrap, totalCents, largeCents, smallCents) {
    const textSmall = wrap.dataset.textSmall || 'עוד [amount] לקבל מתנה ראשונה!';
    const textLarge = wrap.dataset.textLarge || '🎁 מתנה ראשונה שלך! עוד [amount] למתנה הבאה';
    const finalText = wrap.dataset.textFinal || '🎁 קיבלת את המתנה שלך!';

    if (totalCents >= largeCents) {
      return finalText;
    }
    if (smallCents > 0 && totalCents >= smallCents) {
      const rem = largeCents - totalCents;
      return textLarge.replace('[amount]', formatMoney(rem));
    }
    const nextTarget = (smallCents > 0 && totalCents < smallCents) ? smallCents : largeCents;
    const rem = nextTarget - totalCents;
    const textToUse = (smallCents > 0) ? textSmall : textLarge;
    return textToUse.replace('[amount]', formatMoney(rem));
  }

  // ── Confetti ───────────────────────────────────────────────────────────
  function triggerConfetti() {
    if (window.confetti) {
      fireConfetti();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.6.0/dist/confetti.browser.min.js';
    script.onload = fireConfetti;
    document.head.appendChild(script);
  }

  function fireConfetti() {
    if (!window.confetti) return;
    const count = 150;
    const defaults = { origin: { y: 0.8 }, zIndex: 999999, colors: ['#BC9EF4', '#FFFFFF', '#000000'] };

    function fire(particleRatio, opts) {
      confetti(Object.assign({}, defaults, opts, {
        particleCount: Math.floor(count * particleRatio)
      }));
    }

    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
    fire(0.1, { spread: 120, startVelocity: 45 });
  }

  // ── Circle reached state ───────────────────────────────────────────────
  let lastReachedTiers = 0;

  function updateCircles(wrap, totalCents, largeCents, smallCents, isInit = false) {
    const REACHED = 'gfb-pin--reached';
    const pinSmall = wrap.querySelector('.gfb-pin--small');
    const pinLarge = wrap.querySelector('.gfb-pin--large');
    
    let currentTiers = 0;
    if (smallCents > 0 && totalCents >= smallCents) currentTiers++;
    if (totalCents >= largeCents) currentTiers++;

    // Trigger confetti if we went up a tier and it's not the initial cart load
    if (!isInit && currentTiers > lastReachedTiers) {
      triggerConfetti();
    }
    lastReachedTiers = currentTiers;

    if (pinSmall) {
      pinSmall.classList.toggle(REACHED, smallCents > 0 && totalCents >= smallCents);
    }
    if (pinLarge) {
      pinLarge.classList.toggle(REACHED, totalCents >= largeCents);
    }
  }

  // ── Fill animation ─────────────────────────────────────────────────────
  function setFillInstant(fillEl, pct) {
    if (!fillEl) return;
    fillEl.style.setProperty('background', COLOR, 'important');
    fillEl.style.setProperty('transform-origin', 'right center', 'important');
    fillEl.style.transition = 'none';
    fillEl.style.setProperty('transform', `scaleX(${pct / 100})`, 'important');
    void fillEl.offsetWidth;
    fillEl.style.transition = ANIM;
  }

  function animateFill(fillEl, pct) {
    if (!fillEl) return;
    fillEl.style.setProperty('background', COLOR, 'important');
    fillEl.style.setProperty('transform-origin', 'right center', 'important');
    fillEl.style.transition = ANIM;
    requestAnimationFrame(() => {
      fillEl.style.setProperty('transform', `scaleX(${pct / 100})`, 'important');
    });
  }

  // ── Restore after DOM swap ─────────────────────────────────────────────
  function restoreOnDomSwap() {
    const els = getEls();
    if (!els || !els.fill) return;
    if (els.fill !== lastFillEl) {
      lastFillEl = els.fill;
      if (lastProgress !== null) {
        setFillInstant(els.fill, lastProgress);
      }
    }
  }

  // ── Apply cart data ────────────────────────────────────────────────────
  function applyCart(cart) {
    const els = getEls();
    if (!els || !els.fill) return;

    let totalCents = Number(cart.total_price || 0);
    const itemCount  = Number(cart.item_count  || 0);

    // Exclude insurance variant from gift bar calculation
    const excludeVariant = els.wrap && els.wrap.dataset.gfbExcludeVariant;
    if (excludeVariant && cart.items) {
      const exId = Number(excludeVariant);
      cart.items.forEach(function(item) {
        if (item.variant_id === exId) totalCents -= item.final_line_price;
      });
      if (totalCents < 0) totalCents = 0;
    }
    const { large, small, fill, text, wrap } = els;

    // Empty cart → reset
    if (itemCount === 0) {
      setFillInstant(fill, 0);
      lastProgress  = 0;
      lastItemCount = 0;
      lastFillEl    = fill;
      return;
    }

    if (lastItemCount === null) lastItemCount = itemCount;

    const becameNonEmpty = (lastItemCount === 0 && itemCount > 0);
    lastItemCount = itemCount;

    const target = computeProgress(totalCents, large);

    // Update text
    if (text) text.textContent = buildText(wrap, totalCents, large, small);

    // Update circle reached states
    updateCircles(wrap, totalCents, large, small, false);

    if (becameNonEmpty) {
      setFillInstant(fill, 0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          animateFill(fill, target);
          lastProgress = target;
        });
      });
      return;
    }

    animateFill(fill, target);
    lastProgress = target;
  }

  // ── Fetch cart ─────────────────────────────────────────────────────────
  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    return res.json();
  }

  // ── Throttled updater ──────────────────────────────────────────────────
  async function runOnceAfterSettle() {
    if (inFlight) { pending = true; return; }
    inFlight = true;
    try {
      const cart = await fetchCart();
      const itemCount = Number(cart.item_count || 0);
      if (itemCount === 0) {
        lastProgress  = 0;
        lastItemCount = 0;
        lastFillEl    = null;
      }
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { applyCart(cart); });
      });
    } finally {
      inFlight = false;
      if (pending) { pending = false; scheduleAfterSettle(); }
    }
  }

  function scheduleAfterSettle() {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(runOnceAfterSettle, SETTLE_MS);
  }

  // ── Init ───────────────────────────────────────────────────────────────
  function initState() {
    fetchCart()
      .then(cart => {
        const itemCount = Number(cart.item_count || 0);
        if (itemCount === 0) {
          lastProgress  = 0;
          lastItemCount = 0;
          lastFillEl    = null;
          const els = getEls();
          if (els && els.fill) setFillInstant(els.fill, 0);
          return;
        }
        const els = getEls();
        if (!els || !els.fill) return;

        let totalCents = Number(cart.total_price || 0);
        // Exclude insurance variant from gift bar calculation
        const exV = els.wrap && els.wrap.dataset.gfbExcludeVariant;
        if (exV && cart.items) {
          const exId = Number(exV);
          cart.items.forEach(function(it) {
            if (it.variant_id === exId) totalCents -= it.final_line_price;
          });
          if (totalCents < 0) totalCents = 0;
        }
        lastItemCount = itemCount;

        const p = computeProgress(totalCents, els.large);
        setFillInstant(els.fill, p);
        lastProgress = p;
        lastFillEl   = els.fill;

        if (els.text) els.text.textContent = buildText(els.wrap, totalCents, els.large, els.small);
        updateCircles(els.wrap, totalCents, els.large, els.small, true);
      })
      .catch(() => {});
  }

  // ── Bind ───────────────────────────────────────────────────────────────
  function bind() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const mo = new MutationObserver(() => {
      restoreOnDomSwap();
      scheduleAfterSettle();
    });

    mo.observe(drawer, { childList: true, subtree: true });
    initState();
  }

  document.addEventListener('DOMContentLoaded', bind);
  if (document.readyState !== 'loading') bind();
})();